import { SupportedLanguage } from '@goldcast/api/backend';

import { EventStageState, ProfileDropdownLink } from './types';
import { EventActions } from '../user/types';

import type { AgendaItem, Booth, Widget } from '../event/types';

import { getEnv, MIN_RECORD_LATER_BUFFER_MS, RECORDING_STUDIO_STATE } from '@/constants';
import i18n from '@/i18n';
import { formatDuration, showUserModal } from '@/services/event.service';
import loggerService from '@/services/logger.service';
import { UserRole } from '@/services/user.service';
import store, { RootState } from '@/store';
import { findCurrentTrack, itemInTrack } from '@/store/modules/event/getters';
import { isAfter, isBefore } from '@/utils/dateTime';
import {
  formatAgendaItemForStage,
  getCorrectedRole,
  getStageConfigForEventEnd,
  isAgendaItemCurrentlyOpen,
  isTransitionEnabled,
  trackLanguageSwitch
} from '@/utils/stage';
import { getTruncatedText } from '@/utils/stringsUtil';
import { SIDEBAR_VISIBILITY_PERIOD } from '@/views/event/constants';

const EXCLUDED_LANGUAGE_CODES: readonly string[] = ['zh-TW', 'pt-BR', 'es-MX'];

const getters = {
  submenubar(state: EventStageState, getters: any, rootState: RootState, rootGetters: any) {
    //@gc-todo: remove this hack
    const route = rootState.route.name ?? '';
    const page = route.toString().split('-');
    const routeName = page.length >= 2 ? page[1] : page[0];
    const description = rootState.event.event?.tab_descriptions || {};
    const demoButtonText = getTruncatedText(rootState.event.event.demo_request_button_text, 30, true);

    const joinBoothText = '@@translate@@ Join a booth to know more about a sponsor';
    const inBoothText = '@@translate@@ You are in the booth';
    let primaryBoothDescription = i18n.global.t(inBoothText);

    if (routeName === 'EventBoothsGridPage') {
      primaryBoothDescription = i18n.global.t(description.primary_booth || joinBoothText);
    }

    const widgetLinks =
      rootState.event.widgets?.map((widget: Widget) => {
        return {
          label: widget.tab_title || widget.widget?.title || 'Embedded',
          name: `EmbeddedWidget`,
          params: { widgetId: widget.id },
          show: widget.enabled,
          loading: false,
          active: routeName === 'EmbeddedWidget' && rootState.route.params.widgetId === widget.id,
          description: widget.configuration?.description ?? ''
        };
      }) || [];
    const showPrimaryBoothsTab: boolean = Boolean(
      rootState.event.event?.show_event_booths &&
        rootState.event.event.booths.some((booth: Booth) => booth.booth_type === 'Primary')
    );
    const showSecondaryBoothsTab: boolean = Boolean(
      rootState.event.event?.show_event_booths &&
        rootState.event.event?.booth_tab_title_2 &&
        rootState.event.event.booths.some((booth: Booth) => booth.booth_type === 'Secondary')
    );
    let showPeopleTab: boolean = false;

    if (rootState.event.event?.show_attendees) {
      showPeopleTab = !rootState.event.event?.on_demand_mode;
    } else {
      showPeopleTab =
        rootGetters['event/isOrganizerOrSpeaker'] &&
        rootState.event.event?.show_people_tab_speakers &&
        !rootState.event.event?.on_demand_mode;
    }
    const isMobile = window.mobileCheck && window.mobileCheck();
    const noOfRecordedAgendaItem = rootGetters['event/allOnDemandBroadcasts']?.length;
    const showAgenda = rootGetters['filterBar/EventAgendaPage'].totalCount > 0 && !rootGetters['event/isWebinar'];

    return {
      links: [
        {
          label: i18n.global.t('@@translate@@ Stage'),
          name: 'stageRoll',
          count: 0,
          show: true,
          loading: false,
          active: routeName === 'Broadcast' || routeName === 'stageRoll'
        },
        {
          label: i18n.global.t('@@translate@@ Agenda'),
          name: 'EventAgendaPage',
          count: rootGetters['filterBar/EventAgendaPage'].totalCount || 0,
          show: rootState.event.event?.on_demand_mode ? showAgenda && noOfRecordedAgendaItem : showAgenda,
          loading: rootGetters['filterBar/EventAgendaPage'].loading,
          active: routeName === 'EventAgendaPage',
          description: '',
          notificationText: rootGetters['event/currentlyOtherLiveSessions']?.length,
          hoverComponent: 'CurrentLiveSessions',
          showHoverComponent: rootGetters['event/currentlyOtherLiveSessions']?.length && !isMobile,
          props: {
            sessions: rootGetters['event/currentlyOtherLiveSessions'],
            currentSession: rootGetters['event/getCurrentRouteAgendaItem']
          }
        },
        {
          label: i18n.global.t(rootState.event.event?.discussion_room_tab_title || '@@translate@@ Rooms'),
          name: 'DiscussionGroups',
          show:
            rootGetters['event/currentEvent'].show_discussion_rooms &&
            rootGetters['filterBar/DiscussionGroups'].totalCount > 0,
          active: routeName === 'DiscussionGroups',
          count: rootGetters['filterBar/DiscussionGroups'].totalCount,
          loading: rootGetters['filterBar/DiscussionGroups'].loading,
          description: i18n.global.t(
            description.rooms ||
              '@@translate@@ Join a round table to discuss moderated topics with your fellow attendees'
          )
        },
        {
          label: i18n.global.t(rootState.event.event?.booth_tab_title || '@@translate@@ Booths'),
          name: 'EventBoothsGridPage',
          show: showPrimaryBoothsTab,
          // rootGetters["event/currentEvent"].show_booths &&
          // (rootGetters["event/currentEventBooths"] || []).length > 0,
          active: routeName === 'EventBoothsGridPage' || routeName === 'EventBooth',
          loading: false, // rootGetters["filterBar/Booths"].loading,
          description: primaryBoothDescription
        },
        {
          label: i18n.global.t(rootState.event.event?.booth_tab_title_2 || '@@translate@@ Secondary Booths'),
          name: 'EventBoothsGridPage2',
          show: showSecondaryBoothsTab,
          active: routeName === 'EventBoothsGridPage2' || routeName === 'EventBooth2',
          loading: false, // rootGetters["filterBar/Booths"].loading,
          description:
            (routeName === 'EventBoothsGridPage2' &&
              i18n.global.t(
                description.secondary_booth || '@@translate@@ Join a booth to know more about a sponsor'
              )) ||
            i18n.global.t('@@translate@@ You are in the booth')
        },
        {
          label: i18n.global.t(rootState.event.event?.people_tab_title || '@@translate@@ People'),
          name: 'EventNetwork',
          show: showPeopleTab,
          loading: rootGetters['filterBar/EventNetwork'].loading,
          active: routeName === 'EventNetwork',
          description: i18n.global.t(description.people || '@@translate@@ Connect with your fellow attendees')
        },
        {
          label: i18n.global.t(rootState.event.event?.speaker_tab_title || '@@translate@@ Speakers'),
          name: 'Speaker',
          show: rootState.event?.event.speaker_tab_visible,
          loading: false,
          active: routeName === 'Speaker',
          description: i18n.global.t(description.speakers || '@@translate@@ Connect with speakers')
        },
        ...widgetLinks
      ],
      filters: rootGetters['filterBar/filters'](routeName) || {
        hideFilters: true
      },
      showPoweredBy: rootGetters['event/showPoweredBy'],
      showDemoLink: rootState.event.event.on_demand_mode
        ? rootState.event.event.on_demand_demo_link_enabled
        : rootState.event.event.demo_link_enabled,
      customDemoCtaButtonUrl: rootState.event.event?.theme_obj?.custom_demo_cta_button_url || '',
      customDemoCtaLinkTo: rootState.event.event?.theme_obj?.custom_demo_cta_link_to || '',
      demoButtonText,
      demoRequested: rootState.user.currentEventUser?.eventUser?.external_demo_requested,
      currentUser: rootGetters.getCurrentUser || { name: '' },
      eventLogo: rootState.event.event.logo_url,
      isHybridEvent: rootGetters['event/isHybrid']
    };
  },
  hideMiniPlayer(state: EventStageState, getters: any, rootState: RootState) {
    if (rootState.route.name === 'EmbeddedWidget') {
      const widget = rootState.event.widgets?.find(widget => widget.id === rootState.route.params.widgetId);

      if (widget?.configuration?.noMiniPlayer) {
        return true;
      }
    }

    return false;
  },
  isCurrentBoothCtaDisabled(state: EventStageState, getters: any, rootState: RootState) {
    return rootState.user.currentEventUser?.eventUser.booth_activities?.find(
      el => el.booth == rootState.route.params.boothID
    )?.cta_clicked;
  },
  isFullScreen(state: EventStageState, getters: any, rootState: RootState) {
    return (
      (rootState.route.fullPath.includes('/stage') || rootState.route.fullPath.includes('/discussion-room')) &&
      state.fullScreen
    );
  },
  isMinimized(state: EventStageState, getters: any, rootState: RootState) {
    if (rootState.route.fullPath.includes('/broadcast') || rootState.route.fullPath.includes('/stage')) {
      return rootState.route.name !== 'Broadcast' && rootState.route.name !== 'stageRoll';
    }
    return false;
  },
  showSidebar(state: EventStageState, getters: any, rootState: RootState, rootGetters: any) {
    const documents = rootGetters['event/currentPageResources']?.length;
    const useHideSidebar = rootState.event.useHideSidebar;

    if (!state.showEngagementPanel) {
      return false;
    }

    if (state.isTVMode) {
      return state.showChatOnTV;
    }

    if (rootState.event.event.event_type === 'Recording' || rootState.route.query?.record_mode === 'true') {
      return false;
    }

    if (
      useHideSidebar &&
      new Date(rootState.event.event.end_time).getTime() < new Date().getTime() &&
      !documents &&
      (rootState.route.fullPath.includes('/stage') ||
        rootState.route.fullPath.includes('/discussion-room') ||
        rootState.route.fullPath.includes('/eventbooth'))
    ) {
      return new Date(rootState.event.event.end_time).getTime() > new Date().getTime() - SIDEBAR_VISIBILITY_PERIOD;
    }
    return (
      !state.isLiveStreamMode &&
      !getters.isFullScreen &&
      rootState.event.event.event_type !== 'Recording' &&
      (rootState.route.fullPath.includes('/stage') ||
        rootState.route.fullPath.includes('/discussion-room') ||
        rootState.route.fullPath.includes('/eventbooth'))
    );
  },

  stageParams(state: EventStageState, { isMinimized, submenubar }: any, rootState: RootState, rootGetters: any) {
    return {
      minimized: isMinimized,
      activePage: submenubar.links.find((sm: any) => sm.active),
      filters: submenubar.filters,
      subscribedTrack: rootGetters['event/userTrack']
    };
  },
  currentAgendaItem: (state: EventStageState, getters: any, rootState: RootState, rootGetters: any) => {
    const agendaItem: AgendaItem = [...rootGetters['event/userTrackAgenda']]
      .reverse()
      .find(({ is_live }: AgendaItem) => is_live);
    if (agendaItem?.type === 'DISCUSSION_GROUP' && !rootState.event.event.show_discussion_rooms) {
      return undefined;
    }

    if (agendaItem?.type === 'BOOTH' && !rootState.event.event.show_event_booths) {
      return undefined;
    }

    if (agendaItem?.type === 'BOOTH2' && rootState.event.event.booth_tab_title_2 === null) {
      return undefined;
    }
    return agendaItem;
  },
  currentRedirectableAgendaItem: (state: EventStageState, getters: any, rootState: RootState, rootGetters: any) => {
    const agendaItem: AgendaItem = [...rootGetters['event/userTrackAgenda']].reverse().find((item: AgendaItem) => {
      if (item?.type === 'DISCUSSION_GROUP' && !rootState.event.event.show_discussion_rooms) {
        return false;
      }

      if (item?.type === 'BOOTH' && !rootState.event.event.show_event_booths) {
        return false;
      }

      if (item?.type === 'BOOTH2' && rootState.event.event.booth_tab_title_2 === null) {
        return false;
      }

      if (item?.type === 'BREAK') {
        return false;
      }

      if (item.is_live) {
        return true;
      }
    });

    return agendaItem;
  },
  nextAgendaItem: (state: EventStageState, getters: any, rootState: RootState, rootGetters: any) => {
    const isManualRedirectEnabled = rootState.event?.event?.manual_redirection_enabled;
    if (isManualRedirectEnabled) {
      return rootGetters['event/userTrackAgenda'].find(
        ({ live_start_time, live_end_time, is_live, end_time }: AgendaItem) => {
          const wasLiveEarlier = live_start_time && live_end_time;
          const upcomingSession = live_start_time && !live_end_time;
          const isInFuture = isBefore(state.heartBeatDate, end_time);
          return !is_live && !wasLiveEarlier && (upcomingSession || isInFuture);
        }
      );
    }
    return rootGetters['event/userTrackAgenda'].find(
      ({ start_time, is_live }: AgendaItem) => !is_live && isBefore(state.heartBeatDate, start_time)
    );
  },
  currentStage(state: EventStageState, getters: any, rootState: RootState, rootGetters: any) {
    const current = getters.currentAgendaItem;
    const next = getters.nextAgendaItem;
    const formatAgendaItem = formatAgendaItemForStage(rootState, rootGetters);
    const breakItem = formatAgendaItem(
      {
        id: '',
        allowed_end_time: '',
        live_start_time: '',
        live_end_time: '',
        start_time: '',
        end_time: '',
        title: rootState.event.event.title || i18n.global.t('@@translate@@ Break'),
        type: 'BREAK',
        event: '',
        is_live: false,
        sort_priority: 0,
        disabled: false,
        tickets: [],
        tracks: []
      },
      false
    );

    const isAgendaLoaded = rootGetters['event/userTrackAgenda'].length > 0 || rootGetters['event/userTrack']?.id;

    if (
      isBefore(state.heartBeatDate, rootState.event.event.start_time) &&
      rootState.event.event.event_type !== 'Recording'
    ) {
      return {
        is: 'EventScreenSaver',
        event: rootGetters['event/eventSummary'],
        pre: true,
        duration: formatDuration(state.heartBeatDate, rootState.event.event.start_time),
        ctaText:
          rootGetters['filterBar/EventAgendaPage'].totalCount > 0 && !rootGetters['event/isWebinar']
            ? i18n.global.t('@@translate@@ See Agenda')
            : ''
      };
    }

    if (rootState.event.event.event_type === 'Recording') {
      const currentBroadcast = rootGetters['event/currentBroadcast'];
      const recordingState = currentBroadcast?.recording_studio_state;
      const isRecorderRunning = currentBroadcast?.is_recording;

      const startingLater =
        new Date(currentBroadcast?.start_time).getTime() - MIN_RECORD_LATER_BUFFER_MS >
        (rootState.event.stage?.heartBeatDate || new Date()).getTime();
      const isUsingRecorder = rootState.event.event.record_broadcasts;

      const canStartRecording = isRecorderRunning || !isUsingRecorder;

      if (
        recordingState === RECORDING_STUDIO_STATE.STARTING ||
        (!isRecorderRunning && recordingState === RECORDING_STUDIO_STATE.NOT_INITIALIZED && isUsingRecorder)
      ) {
        return {
          is: 'RecordingScreenSaver',
          logo: rootState.event.event.logo_url,
          message: i18n.global.t('@@translate@@ Get Ready!'),
          subMessage: i18n.global.t(
            `@@translate@@ Once we’re done setting up, you can go on stage and hit “Record” to begin capturing content.`
          ),
          duration: formatDuration(state.heartBeatDate, rootState.event.event.start_time),
          pre: true,
          startingLater
        };
      }
      if (recordingState === RECORDING_STUDIO_STATE.PAUSED) {
        return {
          is: 'RecordingScreenSaver',
          message: i18n.global.t('@@translate@@ Recording is paused'),
          subMessage: i18n.global.t('@@translate@@ You can resume recording by clicking the "Resume" button'),
          logo: rootState.event.event.logo_url
        };
      }
      if (recordingState === RECORDING_STUDIO_STATE.STOPPED) {
        return {
          is: 'RecordingScreenSaver',
          post: true,
          message: i18n.global.t('@@translate@@ Your Recording Has Ended'),
          subMessage: i18n.global.t(
            `@@translate@@ Make sure your recording is fully uploaded before closing your browser tab.`
          )
        };
      }
      if (recordingState === RECORDING_STUDIO_STATE.RECORDING) {
        return {
          is: 'RecordingScreenSaver',
          message: i18n.global.t('@@translate@@ Ready to get started?'),
          subMessage: i18n.global.t('@@translate@@ Go on stage to begin capturing content.'),
          logo: rootState.event.event.logo_url,
          duration: formatDuration(state.heartBeatDate, rootState.event.event.start_time),
          pre: false
        };
      }
      if (canStartRecording && recordingState === RECORDING_STUDIO_STATE.NOT_INITIALIZED) {
        return {
          is: 'RecordingScreenSaver',
          message: i18n.global.t('@@translate@@ Ready to get started?'),
          subMessage: i18n.global.t('@@translate@@ Go on stage and hit "Record" to begin capturing content.'),
          logo: rootState.event.event.logo_url,
          duration: formatDuration(state.heartBeatDate, rootState.event.event.start_time),
          pre: !isUsingRecorder && startingLater,
          startingLaterWithoutRecorder: !isUsingRecorder && startingLater
        };
      }

      return {
        is: 'RecordingScreenSaver',
        message: '',
        subMessage: '',
        logo: rootState.event.event.logo_url,
        pre: false
      };
    }
    if (current) {
      return formatAgendaItem(current);
    }
    if (next) {
      return formatAgendaItem(next, true);
    }
    if (isAfter(state.heartBeatDate, rootState.event.event.end_time)) {
      return getStageConfigForEventEnd(rootState, rootGetters);
    }
    if (isAgendaLoaded) {
      return breakItem;
    }
  },
  stageTitle(state: EventStageState, getters: any, rootState: RootState) {
    const { event, roomsItem, breakItem, broadcast, upNext, pre, ctaText, duration, thankYou } =
      getters.currentStage || {};

    const room = roomsItem || breakItem || broadcast || event;

    return {
      ...room,
      title: `${upNext ? i18n.global.t('@@translate@@ UP NEXT') + ': ' : ''}${
        thankYou ? i18n.global.t('@@translate@@ Ended') + ': ' : ''
      }${room?.title || rootState.event.event.title}`,
      subText: duration || room?.start_time || room?.end_time || thankYou,
      pre,
      upNext,
      ctaText: getters.canShowCurrentSessionToast ? '' : ctaText,
      duration,
      thankYou
    };
  },
  getEnableCaptionsFromLocalStorage: (state: EventStageState) => (channel: string) => {
    return JSON.parse(window.localStorage.getItem(`enableCaptions-${channel}`) || 'false');
  },
  isDiffCaptionsLanguageSelected(state: EventStageState, _, rootState: RootState, rootGetters: any): boolean {
    const captionLanguage = state?.captionLanguage;
    const default_language = rootState?.event?.event?.default_language;
    const supportedCaptionsLanguageList = rootGetters['event/supportedCaptionsLanguageList'];
    const selectedCaptionObject = supportedCaptionsLanguageList?.find(elm => elm.value == captionLanguage);

    return Boolean(
      default_language &&
        selectedCaptionObject &&
        selectedCaptionObject.value &&
        default_language !== selectedCaptionObject.value
    );
  },
  canShowCurrentSessionToast(state: EventStageState, getters: any, rootState: RootState, rootGetters: any) {
    const currentTrack = findCurrentTrack(rootState.event.event.tracks, rootState.user.currentEventUser?.track);
    const agendaItem = getters.currentAgendaItem;

    if (!agendaItem) {
      loggerService.info('There is no current broadcast, cannot show toast');
      return false;
    }
    if (isAgendaItemCurrentlyOpen(rootState, rootGetters)) {
      loggerService.info('Active agenda item is currently open, so no need to show toast');
      return false;
    }

    loggerService.info("User's track: ", currentTrack);

    const itemIsInTrack = itemInTrack(currentTrack, agendaItem);
    const isSpeakerInBroadcast = rootGetters['event/currentBroadcast']?.speakerIds?.includes(
      rootState.user.currentEventUser?.id
    );
    const event_role = getCorrectedRole({ rootGetters, rootState });
    const isAutoRedirectEnabled = rootState.event?.event?.is_auto_redirections_enabled || rootState.route.query.tv_mode;

    return (
      isTransitionEnabled(rootState.event, agendaItem) &&
      [UserRole.BROADCAST_SPEAKER, UserRole.EVENT_MEMBER, UserRole.SPONSOR].includes(event_role) &&
      !isAutoRedirectEnabled &&
      itemIsInTrack &&
      !isSpeakerInBroadcast
    );
  },
  profileLinks: (state: EventStageState, getters: any, rootState: RootState, rootGetters: any) => {
    const isOrganizer = rootGetters['isOrganizer'];
    const isEventRolesEnabled = rootState.event.organization?.is_event_roles_enabled;
    const viewAsAttendee = rootGetters['viewAsAttendee'];
    const isEventOrganizer = rootGetters['isEventOrganizer'];
    const liveEventUserCount = rootGetters['event/liveEventUserCount'];
    const isProducer = rootGetters['isProducer'];
    const currentUserId = rootState.user.currentEventUser?.id;
    let currentEventUser = rootState.user.currentEventUser?.eventUser;
    const isInPerson = currentEventUser?.is_in_person;

    if (currentUserId && rootState.event.event.users[currentUserId]) {
      currentEventUser = rootState.event.event.users[currentUserId].eventUser;
    }

    const eventInformationLinks: ProfileDropdownLink[] = [];
    const links: ProfileDropdownLink[] = [];
    const showProducerProfileLinks = (isEventRolesEnabled ? isProducer : isOrganizer) && !state.isLiveStreamMode;

    if (showProducerProfileLinks) {
      if (isEventRolesEnabled) {
        eventInformationLinks.push({
          label: i18n.global.t('@@translate@@ Role'),
          value: i18n.global.t('@@translate@@ Producer')
        });
      } else {
        eventInformationLinks.push({
          label: i18n.global.t('@@translate@@ Role'),
          value: i18n.global.t('@@translate@@ Organizer')
        });
      }
      if (isEventOrganizer) {
        if (rootGetters['isEventActionPermitted'](EventActions.ControlEventTicker)) {
          const showTicker = rootState.event.event.show_ticker;
          const stop = () => store.dispatch('event/stopEventTicker', null, { root: true });
          const start = () => store.dispatch('event/openTickerDialog', null, { root: true });
          links.push({
            label: i18n.global.t('@@translate@@ Make an Announcement'),
            links: [
              {
                label: i18n.global.t('@@translate@@ Ticker'),
                type: 'button',
                buttonText: showTicker ? i18n.global.t('@@translate@@ STOP') : i18n.global.t('@@translate@@ CREATE'),
                action: showTicker ? stop : start
              }
            ]
          });
        }

        if (liveEventUserCount.length === 1) {
          // Show single session count
          const sessionValue = liveEventUserCount[0]?.value;
          if (sessionValue !== undefined && sessionValue !== ' ') {
            eventInformationLinks.push({
              label: i18n.global.t('@@translate@@ Session Count'),
              value: sessionValue,
              tooltip: i18n.global.t(
                '@@translate@@ Total number of attendees currently viewing this session Note: This is for organizer eyes only'
              )
            });
          } else {
            // Show skeleton loader for session count
            eventInformationLinks.push({
              label: i18n.global.t('@@translate@@ Session Count'),
              type: 'skeleton',
              tooltip: i18n.global.t('@@translate@@ Loading session count... Note: This is for organizer eyes only'),
              ariaLabel: 'Loading session attendees count'
            });
          }
        } else if (liveEventUserCount.length > 1) {
          eventInformationLinks.push({
            label: i18n.global.t('@@translate@@ Session Count'),
            tooltip: i18n.global.t(
              '@@translate@@ The number of attendees currently viewing live sessions. Note: This is for organizer eyes only'
            ),
            links: liveEventUserCount
          });
        }

        const totalAttendeeAttr = rootState.event.event?.use_mm_sockets
          ? 'activeUserCountUsingMmid'
          : 'activeUserCount';
        const totalAttendeeCount = rootState.event.event[totalAttendeeAttr]?.ALL;

        if (totalAttendeeCount !== undefined) {
          // Show actual count
          eventInformationLinks.push({
            label: i18n.global.t('@@translate@@ Total Attendees'),
            value: `${totalAttendeeCount}`,
            tooltip: i18n.global.t(
              '@@translate@@ Total number of attendees online at this time Note: This is for organizer eyes only'
            ),
            ariaLabel: `Total number of attendees currently viewing this event is ${totalAttendeeCount}`
          });
        } else {
          // Show skeleton loader
          eventInformationLinks.push({
            label: i18n.global.t('@@translate@@ Total Attendees'),
            type: 'skeleton',
            tooltip: i18n.global.t('@@translate@@ Loading attendee count... Note: This is for organizer eyes only'),
            ariaLabel: 'Loading total attendees count'
          });
        }
      }
      if (rootGetters['isEventActionPermitted'](EventActions.ViewAttendeeView)) {
        eventInformationLinks.push({
          label: i18n.global.t(
            viewAsAttendee ? '@@translate@@ Turn off Attendee mode' : '@@translate@@ See Attendee View'
          ),
          type: 'toggle',
          selected: viewAsAttendee,
          action: () => store.dispatch('event/stage/updateUserMode', !viewAsAttendee),
          testmarker: 'attendeetoggle'
        });
      }
    }

    if (currentEventUser?.event_hub_link) {
      eventInformationLinks.push({
        label: i18n.global.t('@@translate@@ Survey Link'),
        type: 'icon-action',
        icon: 'link',
        action: () => window.open(currentEventUser?.event_hub_link, '__blank'),
        size: 'x-small',
        tooltip: i18n.global.t('@@translate@@ Go to Event Hub'),
        bg: true
      });
    }

    if (
      rootGetters['isEventActionPermitted'](EventActions.StartInPersonStreaming) &&
      rootState.event.event.is_hybrid &&
      !rootState.event.event.on_demand_mode &&
      !viewAsAttendee
    ) {
      eventInformationLinks.push({
        label: i18n.global.t(
          state.isLiveStreamMode ? '@@translate@@ Stop In-Person Streaming' : '@@translate@@ Start In-Person Streaming'
        ),
        type: 'toggle',
        selected: state.isLiveStreamMode,
        action() {
          store.dispatch('event/stage/toggleSetupMode');
        }
      });
    }

    if (isOrganizer && rootState.event.event.is_hybrid && rootState.event.event.internal_session_checkin) {
      const inPersonManagerUrl = getEnv('VUE_APP_INPERSONMANAGER');
      const eventId = rootState.route.params.eventID;
      eventInformationLinks.push({
        label: i18n.global.t('@@translate@@ InPerson CheckIn App'),
        type: 'icon-action',
        icon: 'link',
        action: () => window.open(`${inPersonManagerUrl}/e/${eventId}`, '_self'),
        size: 'x-small',
        tooltip: i18n.global.t('@@translate@@ App to Check-In In-Person attendees'),
        bg: true
      });
    }

    const hybridEventAttendenceLinks: ProfileDropdownLink[] = [];
    if (rootGetters['event/isHybrid']) {
      let attendingMsg = i18n.global.t('@@translate@@ Attending Virtually');
      if (isInPerson) {
        attendingMsg = i18n.global.t('@@translate@@ Attending In Person');
      }
      hybridEventAttendenceLinks.push({
        label: attendingMsg,
        type: 'icon-action',
        icon: isInPerson ? 'in-person-attendee' : 'virtual-attendee',
        action: () => store.dispatch('event/showHybridModal'),
        size: 'x-small',
        tooltip: i18n.global.t('@@translate@@ Click to change your attendance type'),
        bg: true,
        testmarker: 'attendeeinpersontoggle'
      });
    }

    return {
      open: state.userProfileDropdownOpen,
      links: [
        {
          label: i18n.global.t('@@translate@@ My Profile'),
          action: () => showUserModal(rootState.user.currentUser),
          testmarker: 'profilebutton'
        },
        ...hybridEventAttendenceLinks,
        {
          label: i18n.global.t('@@translate@@ Languages'),
          testmarker: 'languagemenu',
          links: rootState.event.event?.events_app_languages
            ?.filter(({ code }: SupportedLanguage) => !EXCLUDED_LANGUAGE_CODES.includes(code))
            ?.map(({ native_name: language, code, name: ariaLabel, ...obj }: SupportedLanguage) => {
              const selected = rootState.event.translator.selectedLocale === code;
              return {
                label: language,
                type: 'tick',
                selected,
                ariaLabel,
                action: () => {
                  if (!selected) {
                    trackLanguageSwitch({
                      language,
                      ariaLabel,
                      code,
                      ...obj
                    }).then(() => {
                      const event = store.state.event.event;
                      localStorage.setItem(`${event.id}-current-language`, code);
                      store.dispatch('event/setLocale', event, { root: true });
                    });
                  }
                },
                testmarker: 'language'
              };
            })
        },
        ...links
      ],
      eventInformationLinks,
      bottomLinks: [
        ...(rootGetters['isEventActionPermitted'](EventActions.END_EVENT) && !rootState.event.event.on_demand_mode
          ? [
              {
                label: i18n.global.t('@@translate@@ Close Event'),
                action: () => store.dispatch('event/closeEvent'),
                bg: true
              }
            ]
          : []),
        {
          label: i18n.global.t('@@translate@@ Logout'),
          action: () => store.dispatch('event/logout'),
          testmarker: 'logoutbutton'
        }
      ]
    };
  },
  areSocketsConnected(state: EventStageState, getters: any, rootState: RootState) {
    const mattermostSocketStatus = rootState.mattermost.socketstatus;
    const gatewaySocketStatus = rootState.apigateway.socketstatus;
    const eventSourceStatus = rootState.eventsource?.eventSourceStatus;
    return mattermostSocketStatus === 'online' || gatewaySocketStatus === 'online' || eventSourceStatus === 'connected';
  },
  socketStatus(state: EventStageState, getters: any, rootState: RootState) {
    const mattermostSocketStatus = rootState.mattermost.socketstatus;
    const gatewaySocketStatus = rootState.apigateway.socketstatus;
    const eventSourceStatus = rootState.eventsource?.eventSourceStatus;
    if (mattermostSocketStatus === 'error' || gatewaySocketStatus === 'error' || eventSourceStatus === 'error') {
      return 'error';
    }
    if (mattermostSocketStatus === 'offline' || gatewaySocketStatus === 'offline' || eventSourceStatus === 'closed') {
      return 'offline';
    }
    if (mattermostSocketStatus === 'online' || gatewaySocketStatus === 'online' || eventSourceStatus === 'connected') {
      return 'online';
    }
    return 'offline';
  },
  currentBroadcastOverlay(state: EventStageState, getters: any, rootState: RootState, rootGetters: any) {
    const broadcast = rootGetters['event/currentBroadcast'];
    return {
      overlay_text: broadcast?.overlay_text,
      show_overlay: broadcast?.show_overlay && !getters.isLiteMode,
      logo: rootState.event.event.logo_url
    };
  },
  isLiteMode(state: EventStageState, getters: any, rootState: RootState, rootGetters: any) {
    const canAccessBackstage =
      rootGetters['event/isSpeaker'] || (rootGetters['event/isOrganizer'] && !rootState.event.stage?.isTVMode);
    const isHybridOrLive = !['SIMULIVE', 'LIVE_EXTERNAL'].includes(
      rootGetters['event/currentBroadcast'].broadcast_type
    );
    return rootState.user.liteMode.liteModeActive && !canAccessBackstage && isHybridOrLive;
  },
  showNewStageControls(state: EventStageState, getters: any, rootState: RootState, rootGetters: any) {
    const isMobile = window.mobileCheck && window.mobileCheck();
    return Boolean(
      !isMobile &&
        rootGetters['event/isBroadcastSpeakerOrOrganizer'] &&
        (!rootState.event.event.on_demand_mode || (store.state.event.useOnDemandMQL && rootGetters['isOrganizerV2'])) &&
        !rootState.event.stage?.isTVMode &&
        !rootState.event.stage?.isLiveStreamMode
    );
  }
};

export default getters;
export type EventStageGetters = typeof getters;
